import { useState, useCallback, useRef, MouseEvent } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { StaffDailyHours, dailyHoursVariables } from '../utils/staffDailyHours';
import {
  LocationHistoryExportResponse,
  exportLocationHistoryToCSV,
  exportLocationHistoryToXLSX,
} from '../utils/staffExportUtils';

export const STAFF_LOCATION_HISTORY_EXPORT = gql`
  query StaffLocationHistoryExport($startDate: Date!, $endDate: Date!, $dailyStartMinute: Int, $dailyEndMinute: Int) {
    staffLocationHistoryExport(
      startDate: $startDate
      endDate: $endDate
      dailyStartMinute: $dailyStartMinute
      dailyEndMinute: $dailyEndMinute
    ) {
      hospitalName
      entries {
        date
        staffId
        staffName
        role
        roomId
        roomName
        roomType
        enteredAt
        exitedAt
        durationMinutes
        hourlyRate
        cost
      }
      summary {
        totalEntries
        totalCost
        totalHours
      }
    }
  }
`;

export type ExportFormat = 'csv' | 'xlsx';

interface UseStaffExportOptions {
  onError?: (error: Error) => void;
}

interface StaffExportOptions {
  dailyHours?: StaffDailyHours | null;
  hospitalTimezone?: string;
}

interface UseStaffExportReturn {
  exportLoading: boolean;
  exportMenuAnchor: HTMLElement | null;
  handleExportClick: (event: MouseEvent<HTMLElement>) => void;
  handleExportClose: () => void;
  handleExport: (format: ExportFormat, startDate: string, endDate: string, exportOptions?: StaffExportOptions) => void;
}

export const useStaffExport = (options?: UseStaffExportOptions): UseStaffExportReturn => {
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);
  const pendingExport = useRef<(StaffExportOptions & { format: ExportFormat }) | null>(null);

  const [fetchExportData, { loading: exportLoading }] = useLazyQuery<LocationHistoryExportResponse>(
    STAFF_LOCATION_HISTORY_EXPORT,
    {
      fetchPolicy: 'network-only',
      onCompleted: exportData => {
        const request = pendingExport.current;
        if (exportData?.staffLocationHistoryExport && request) {
          const data = { ...exportData.staffLocationHistoryExport, ...request };
          if (request.format === 'csv') {
            exportLocationHistoryToCSV(data);
          } else {
            exportLocationHistoryToXLSX(data);
          }
          pendingExport.current = null;
        }
      },
      onError: err => {
        console.error('Export failed:', err);
        pendingExport.current = null;
        if (options?.onError) {
          options.onError(err);
        } else {
          alert('Failed to export data. Please try again.');
        }
      },
    }
  );

  const handleExportClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  }, []);

  const handleExportClose = useCallback(() => {
    setExportMenuAnchor(null);
  }, []);

  const handleExport = useCallback(
    (format: ExportFormat, startDate: string, endDate: string, exportOptions: StaffExportOptions = {}) => {
      setExportMenuAnchor(null);
      pendingExport.current = { ...exportOptions, format };

      fetchExportData({
        variables: {
          startDate,
          endDate,
          ...dailyHoursVariables(exportOptions.dailyHours ?? null),
        },
      });
    },
    [fetchExportData]
  );

  return {
    exportLoading,
    exportMenuAnchor,
    handleExportClick,
    handleExportClose,
    handleExport,
  };
};
